'use client'

import { useState, useEffect, useCallback } from 'react'
import { commissionApi, platformSettingsApi } from '@/lib/api'
import type { PlatformSettings as PlatformSettingsType } from '@/types'


interface PlatformSettingsProps {
  onUpdate: () => void
}

export default function PlatformSettings({ onUpdate }: PlatformSettingsProps) {
  const [settings, setSettings] = useState<PlatformSettingsType>({
    commissionRate: 0.10,
    platformFee: 0,
    maxPropertiesPerLandlord: 10,
    maxApplicationsPerClient: 5,
    autoApproveProperties: false,
    requireKyc: true,
    emailNotifications: true,
    maintenanceMode: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [initialCommissionRate, setInitialCommissionRate] = useState<number>(0.10)

  // Load settings
  const loadSettings = useCallback(async () => {
    setLoading(true)
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
      if (!token) return
      
      // Load full platform settings
      const platformRes = await platformSettingsApi.get(token)
      if (platformRes.success && platformRes.data) {
        setSettings(platformRes.data)
      }

      // Load commission rate from commission API (source of truth for commissionRate)
      const commissionResponse = await commissionApi.getRate(token)
      if (commissionResponse.success && commissionResponse.data) {
        const commissionRate = (commissionResponse.data as any).commissionRate ?? 0.10
        setSettings(prev => ({ ...prev, commissionRate }))
        setInitialCommissionRate(commissionRate)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  // Save settings
  const saveSettings = async () => {
    setSaving(true)
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
      if (!token) {
        alert('Authentication required')
        return
      }

      // Save commission rate using commission API (requires reason)
      const newRate = settings.commissionRate
      if (newRate !== initialCommissionRate) {
        const reason = prompt('Please provide a reason for changing the commission rate:')
        if (!reason || reason.trim().length === 0) {
          alert('Reason is required for commission rate changes')
          setSaving(false)
          return
        }

        try {
          const commissionResponse = await commissionApi.updateRate(token, newRate, reason.trim())
          if (!commissionResponse.success) {
            alert(commissionResponse.error || 'Failed to update commission rate')
            setSaving(false)
            return
          }
          setInitialCommissionRate(newRate)
        } catch (error: any) {
          alert(error.message || 'Failed to update commission rate')
          setSaving(false)
          return
        }
      }
      
      // Save other platform settings
      const updateRes = await platformSettingsApi.update(token, {
        platformFee: settings.platformFee,
        maxPropertiesPerLandlord: settings.maxPropertiesPerLandlord,
        maxApplicationsPerClient: settings.maxApplicationsPerClient,
        autoApproveProperties: settings.autoApproveProperties,
        requireKyc: settings.requireKyc,
        emailNotifications: settings.emailNotifications,
        maintenanceMode: settings.maintenanceMode,
      })
      if (!updateRes.success) {
        throw new Error(updateRes.error || 'Failed to save platform settings')
      }

      onUpdate()
      alert('Settings saved successfully!')
    } catch (error: any) {
      console.error('Error:', error)
      alert(error.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  // Handle input changes
  const handleInputChange = (field: keyof PlatformSettingsType, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Platform Settings</h2>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Platform Settings</h2>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Commission & Fees */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Commission & Fees</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commission Rate (%)
                </label>
                <input
                  type="number"
                  value={Number((settings.commissionRate ?? 0) * 100)}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : Number(e.target.value)
                    handleInputChange('commissionRate', (Number.isFinite(v) ? v : 0) / 100)
                  }}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage of rent collected as commission (0% = no commission)</p>
                {settings.commissionRate === 0 && (
                  <p className="text-xs text-green-600 mt-1">✓ Commission is currently disabled</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform Fee (₦)
                </label>
                <input
                  type="number"
                  value={Number(settings.platformFee ?? 0)}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : Number(e.target.value)
                    handleInputChange('platformFee', Number.isFinite(v) ? v : 0)
                  }}
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Fixed fee per successful transaction (0 = no fee)</p>
                {settings.platformFee === 0 && (
                  <p className="text-xs text-green-600 mt-1">✓ Platform fee is currently disabled</p>
                )}
              </div>
            </div>
          </div>

          {/* Limits */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">User Limits</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Properties per Landlord
                </label>
                <input
                  type="number"
                  value={Number(settings.maxPropertiesPerLandlord ?? 0)}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                    handleInputChange('maxPropertiesPerLandlord', Number.isFinite(v) ? v : 0)
                  }}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum properties a landlord can list</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Applications per Client
                </label>
                <input
                  type="number"
                  value={Number(settings.maxApplicationsPerClient ?? 0)}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                    handleInputChange('maxApplicationsPerClient', Number.isFinite(v) ? v : 0)
                  }}
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum applications a client can submit</p>
              </div>
            </div>
          </div>

          {/* Approval Settings */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Approval Settings</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoApproveProperties"
                  checked={!!settings.autoApproveProperties}
                  onChange={(e) => handleInputChange('autoApproveProperties', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoApproveProperties" className="ml-2 block text-sm text-gray-900">
                  Auto-approve new properties
                </label>
                <p className="text-xs text-gray-500 ml-2">Properties will be automatically verified without admin review</p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requireKyc"
                  checked={!!settings.requireKyc}
                  onChange={(e) => handleInputChange('requireKyc', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="requireKyc" className="ml-2 block text-sm text-gray-900">
                  Require KYC verification
                </label>
                <p className="text-xs text-gray-500 ml-2">Users must complete KYC before using the platform</p>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Notification Settings</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="emailNotifications"
                  checked={!!settings.emailNotifications}
                  onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                  Enable email notifications
                </label>
                <p className="text-xs text-gray-500 ml-2">Send email notifications for important events</p>
              </div>
            </div>
          </div>

          {/* Maintenance Mode */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">System Settings</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="maintenanceMode"
                  checked={!!settings.maintenanceMode}
                  onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-900">
                  Enable maintenance mode
                </label>
                <p className="text-xs text-red-500 ml-2">Platform will be temporarily unavailable to users</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Settings Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Current Settings Summary</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Commission Rate</h4>
              <p className="text-2xl font-bold text-blue-600">{((settings.commissionRate || 0) * 100).toFixed(1)}%</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Platform Fee</h4>
              <p className="text-2xl font-bold text-green-600">₦{settings.platformFee || 0}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Property Limit</h4>
              <p className="text-2xl font-bold text-purple-600">{settings.maxPropertiesPerLandlord ?? 0}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Application Limit</h4>
              <p className="text-2xl font-bold text-orange-600">{settings.maxApplicationsPerClient ?? 0}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Auto-approve Properties</h4>
              <p className={`text-2xl font-bold ${settings.autoApproveProperties ? 'text-green-600' : 'text-red-600'}`}>
                {settings.autoApproveProperties ? 'Yes' : 'No'}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">KYC Required</h4>
              <p className={`text-2xl font-bold ${settings.requireKyc ? 'text-green-600' : 'text-red-600'}`}>
                {settings.requireKyc ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {new Date((settings.updatedAt || settings.lastUpdatedAt || new Date().toISOString()) as string).toLocaleString()}
      </div>
    </div>
  )
} 


